import type { AriaRole, CSSProperties } from "react";

/**
 * Shape of one migrated SVG figure. The inner content (generated engraving
 * geometry — meridian polylines, tick rings, dimension text) is kept verbatim
 * as a string and injected via dangerouslySetInnerHTML. The OUTER <svg> is a
 * real JSX element carrying its own attributes, and because React never
 * hydrates dangerouslySetInnerHTML content this is inherently free of
 * hydration mismatches.
 */
export type EngravingData = {
  viewBox?: string;
  role?: AriaRole;
  ariaLabel?: string;
  ariaHidden?: boolean;
  className?: string;
  styleText?: string;
  width?: string;
  height?: string;
  inner: string;
};

/** Parse an inline CSS `style="a:b;c:d"` string into a React style object. */
function parseStyleText(text?: string): CSSProperties | undefined {
  if (!text) return undefined;
  const out: Record<string, string> = {};
  for (const decl of text.split(";")) {
    const idx = decl.indexOf(":");
    if (idx === -1) continue;
    const prop = decl.slice(0, idx).trim();
    const value = decl.slice(idx + 1).trim();
    if (!prop) continue;
    const camel = prop.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
    out[camel] = value;
  }
  return out as CSSProperties;
}

/**
 * Render one migrated engraving. `extraClassName` lets a caller add layout
 * classes on top of the figure's own class (e.g. sizing wrappers).
 */
export function Engraving({
  data,
  extraClassName,
  extraStyle,
}: {
  data: EngravingData;
  extraClassName?: string;
  extraStyle?: CSSProperties;
}) {
  const className =
    [data.className, extraClassName].filter(Boolean).join(" ") || undefined;
  const style = { ...parseStyleText(data.styleText), ...extraStyle };
  return (
    <svg
      viewBox={data.viewBox}
      role={data.role}
      aria-label={data.ariaLabel}
      aria-hidden={data.ariaHidden}
      width={data.width}
      height={data.height}
      className={className}
      style={Object.keys(style).length ? style : undefined}
      dangerouslySetInnerHTML={{ __html: data.inner }}
    />
  );
}
